import {
  addDays,
  addYears,
  endOfMonth,
  endOfWeek,
  endOfYear,
  setMonth,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subDays,
  subYears,
} from "date-fns";

export type NamedDateRange =
  | "today"
  | "month"
  | "week"
  | "end-of-week"
  | "quarter"
  | "year"
  | ""
  | "lastDaysAgo"
  | "quarter1"
  | "quarter2"
  | "quarter3"
  | "quarter4"
  | "jan"
  | "feb"
  | "mar"
  | "apr"
  | "may"
  | "jun"
  | "jul"
  | "aug"
  | "sep"
  | "oct"
  | "nov"
  | "dec"
  | "30days"
  | "60days"
  | "180days"
  | "1year"
  | "gte-one-year"
  | "gte-two-years"
  | "gte-three-years"
  | "future"
  | "overdue"
  | "last-week"
  | "last-month"
  | "current-week";

export default function parseFixedDateRange(
  range: NamedDateRange,
  param?: any
) {
  const now = new Date();

  switch (range) {
    case "today":
      return {
        dateFrom: now,
        dateTo: now,
      };
    case "week":
      return {
        dateFrom: startOfWeek(now, {
          weekStartsOn: 1,
        }),
        dateTo: now,
      };
    case "end-of-week":
      return {
        dateFrom: now,
        dateTo: endOfWeek(now, {
          weekStartsOn: 1,
        }),
      };
    case "month":
      return {
        dateFrom: startOfMonth(now),
        dateTo: now,
      };
    case "lastDaysAgo":
      return {
        dateFrom: subDays(now, Number(param) || 1),
        dateTo: now,
      };
    case "jan":
      return {
        dateFrom: startOfMonth(setMonth(now, 0)),
        dateTo: endOfMonth(setMonth(now, 0)),
      };
    case "feb":
      return {
        dateFrom: startOfMonth(setMonth(now, 1)),
        dateTo: endOfMonth(setMonth(now, 1)),
      };
    case "mar":
      return {
        dateFrom: startOfMonth(setMonth(now, 2)),
        dateTo: endOfMonth(setMonth(now, 2)),
      };
    case "apr":
      return {
        dateFrom: startOfMonth(setMonth(now, 3)),
        dateTo: endOfMonth(setMonth(now, 3)),
      };
    case "may":
      return {
        dateFrom: startOfMonth(setMonth(now, 4)),
        dateTo: endOfMonth(setMonth(now, 4)),
      };
    case "jun":
      return {
        dateFrom: startOfMonth(setMonth(now, 5)),
        dateTo: endOfMonth(setMonth(now, 5)),
      };
    case "jul":
      return {
        dateFrom: startOfMonth(setMonth(now, 6)),
        dateTo: endOfMonth(setMonth(now, 6)),
      };
    case "aug":
      return {
        dateFrom: startOfMonth(setMonth(now, 7)),
        dateTo: endOfMonth(setMonth(now, 7)),
      };
    case "sep":
      return {
        dateFrom: startOfMonth(setMonth(now, 8)),
        dateTo: endOfMonth(setMonth(now, 8)),
      };
    case "oct":
      return {
        dateFrom: startOfMonth(setMonth(now, 9)),
        dateTo: endOfMonth(setMonth(now, 9)),
      };
    case "nov":
      return {
        dateFrom: startOfMonth(setMonth(now, 10)),
        dateTo: endOfMonth(setMonth(now, 10)),
      };
    case "dec":
      return {
        dateFrom: startOfMonth(setMonth(now, 11)),
        dateTo: endOfMonth(setMonth(now, 11)),
      };
    case "quarter":
      return {
        dateFrom: startOfQuarter(now),
        dateTo: now,
      };
    case "quarter1":
      return {
        dateFrom: startOfMonth(setMonth(now, 0)),
        dateTo: endOfMonth(setMonth(now, 2)),
      };
    case "quarter2":
      return {
        dateFrom: startOfMonth(setMonth(now, 3)),
        dateTo: endOfMonth(setMonth(now, 5)),
      };
    case "quarter3":
      return {
        dateFrom: startOfMonth(setMonth(now, 6)),
        dateTo: endOfMonth(setMonth(now, 8)),
      };
    case "quarter4":
      return {
        dateFrom: startOfMonth(setMonth(now, 9)),
        dateTo: endOfYear(now),
      };
    case "year":
      return {
        dateFrom: startOfYear(now),
        dateTo: now,
      };
    case "gte-one-year":
      return {
        dateFrom: null,
        dateTo: subYears(now, 1),
      };
    case "gte-two-years":
      return {
        dateFrom: null,
        dateTo: subYears(now, 2),
      };
    case "gte-three-years":
      return {
        dateFrom: null,
        dateTo: subYears(now, 3),
      };
    case "30days":
      return {
        dateFrom: now,
        dateTo: addDays(now, 30),
      };
    case "60days":
      return {
        dateFrom: now,
        dateTo: addDays(now, 60),
      };
    case "180days":
      return {
        dateFrom: now,
        dateTo: addDays(now, 180),
      };
    case "1year":
      return {
        dateFrom: now,
        dateTo: addYears(now, 1),
      };
    case "future":
      return {
        dateFrom: addDays(startOfDay(now), 1),
        dateTo: null,
      };
    case "overdue":
      return {
        dateFrom: addDays(startOfDay(now), -60), //last 60 days
        dateTo: addDays(startOfDay(now), -1),
      };
    case "last-week": {
      const lastSunday = subDays(startOfWeek(now, { weekStartsOn: 1 }), 1);
      return {
        dateFrom: startOfWeek(lastSunday, { weekStartsOn: 1 }),
        dateTo: lastSunday,
      };
    }
    case "current-week": {
      return {
        dateFrom: startOfWeek(now, { weekStartsOn: 1 }),
        dateTo: now,
      };
    }
    case "last-month": {
      const prevMonthDay = subDays(startOfMonth(now), 1);
      return {
        dateFrom: startOfMonth(prevMonthDay),
        dateTo: endOfMonth(prevMonthDay),
      };
    }
    default:
      break;
  }

  return {};
}

const UNIX_MIN_DATE = new Date(0);

export const isDateBeforeUnixMinDate = (
  value: Date | string | number | null | undefined
) => {
  if (!value || new Date(value) < UNIX_MIN_DATE) {
    return true;
  }
  return false;
};
